import { useCallback, useEffect, useState } from 'react';

import {
  RightOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Button, Modal, PageHeader, Row, Tooltip } from 'antd';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { T } from 'react-translator-component';

import * as S from './styled';

import {
  getDynamicPricingDetail,
  updateDynamicPricingDetail,
} from '../../../../hooks/dynamicPricing';
import { ReadOnlyAlert } from '../../../atoms/ReadOnlyAlert/ReadOnlyAlert';
import {
  mascaraMoeda,
  SetPricingPageFormValues,
} from '../../../organisms/SetPricingPageForm/SetPricingPageForm';

type Location = {
  state: {
    profileId: string;
    profileName: string;
    editable?: boolean;
  };
};

const weekDays = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const ProfileCreatePage = () => {
  const history = useHistory();

  const location: Location = useLocation();

  const [dynamicPricingDetail, setDynamicPricingDetail] = useState<any>();

  // Inicia com o valor vindo da listagem para não piscar os botões, e é
  // substituído pelo da resposta do detalhe, que é a fonte da verdade
  const [canEdit, setCanEdit] = useState(!!location.state.editable);

  const getDetail = useCallback(async () => {
    const { dynamicPricingDetailList, editable }: any =
      await getDynamicPricingDetail(location.state.profileId);

    setCanEdit(!!editable);

    if (dynamicPricingDetailList?.length) {
      setDynamicPricingDetail(dynamicPricingDetailList);
    }
  }, [location.state.profileId]);

  useEffect(() => {
    (async () => {
      await getDetail();
    })();
  }, [getDetail]);

  const handleDeleteException = (id: string, startDate: string) => {
    // Guarda: perfil de outro tenant é somente leitura
    if (!canEdit) return;

    Modal.confirm({
      title: startDate
        ? `Deseja mesmo remover a exceção do dia ${moment(
            startDate,
            'YYYY-MM-DD',
          ).format('DD/MM/YYYY')}?`
        : 'Deseja mesmo remover a exceção selecionada?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Excluir',
      cancelText: 'Cancelar',
      okButtonProps: {
        danger: true,
      },
      onOk: async () => {
        await updateDynamicPricingDetail(location.state.profileId, {
          dynamicPricingDetailFormList: dynamicPricingDetail.filter(
            (item) => item.dynamicPricingDetailUuid !== id,
          ),
        });

        await getDetail();
      },
    });
  };

  const isDynamicPricingActive = (day: string) => {
    return dynamicPricingDetail.some((dynamicPricingDetailPeriod) => {
      return (
        dynamicPricingDetailPeriod.active &&
        dynamicPricingDetailPeriod.dayOfWeek === day.toUpperCase() &&
        !dynamicPricingDetailPeriod.general
      );
    });
  };

  return (
    <S.Div>
      <PageHeader
        onBack={() => history.push('/dynamic-pricing')}
        title={location.state.profileName}
      />

      {!canEdit && <ReadOnlyAlert style={{ marginTop: 24 }} />}

      <S.Wrapper>
        <S.Blank>
          <S.Title>Definir precificação</S.Title>
          <S.WrapperWeek>
            {weekDays.map((day, key) => (
              <S.WrapperDay
                key={`dynamic-pricing-${day}-${key}`}
                onClick={() =>
                  history.push({
                    pathname: `/dynamic-pricing/${
                      location.state.profileId
                    }/${day.toLowerCase()}`,
                    state: {
                      dynamicPricingDetail,
                      profileId: location.state.profileId,
                      profileName: location.state.profileName,
                      editable: canEdit,
                    },
                  })
                }
              >
                <S.DayLabel>{T(day)}</S.DayLabel>

                {dynamicPricingDetail?.filter(
                  (dayDetail) => day.toUpperCase() === dayDetail.dayOfWeek,
                ).length > 0 ? (
                  dynamicPricingDetail
                    ?.filter(
                      (dayDetail) =>
                        day.toUpperCase() === dayDetail.dayOfWeek &&
                        !!dayDetail?.general,
                    )
                    .map((dayDetail: any, index) => {
                      return (
                        <S.Content key={`dynamicPricingDetail-${index}`}>
                          <S.Item>
                            <S.Label>Valor de início</S.Label>

                            <S.Value>
                              {dayDetail?.servicePrice !== null &&
                              dayDetail?.servicePrice >= 0
                                ? dayDetail.service_price_income_human_readable
                                : '-'}
                            </S.Value>
                          </S.Item>

                          {dayDetail?.kwhPrice !== null &&
                            !!dayDetail?.kwhPrice && (
                              <S.Item>
                                <S.Label>Preço por kWh</S.Label>

                                <S.Value>
                                  {mascaraMoeda(dayDetail.kwhPrice.toFixed(2))}
                                </S.Value>
                              </S.Item>
                            )}

                          {dayDetail?.durationPrice !== null &&
                            !!dayDetail?.durationPrice && (
                              <S.Item>
                                <S.Label>Preço por tempo</S.Label>

                                <S.Value>
                                  {mascaraMoeda(
                                    dayDetail.durationPrice.toFixed(2),
                                  )}
                                </S.Value>
                              </S.Item>
                            )}

                          <S.Item>
                            <S.Label>Operação</S.Label>

                            <S.Value>
                              {dayDetail?.operationFee !== null &&
                              dayDetail?.operationFee >= 0
                                ? `${dayDetail.operationFee * 100}%`
                                : '-'}
                            </S.Value>
                          </S.Item>

                          <S.Item>
                            <S.Label>% Impostos</S.Label>

                            <S.Value>
                              {dayDetail?.taxes !== null &&
                              dayDetail?.taxes >= 0
                                ? `${dayDetail.taxes * 100}%`
                                : '-'}
                            </S.Value>
                          </S.Item>

                          <S.Item>
                            <S.Label>Penalidade</S.Label>

                            <S.Value>
                              {dayDetail?.idleTimePrice !== null &&
                              dayDetail?.idleTimePrice >= 0
                                ? mascaraMoeda(
                                    dayDetail.idleTimePrice.toFixed(2),
                                  )
                                : '-'}
                            </S.Value>
                          </S.Item>

                          {/* <S.Item>
                            <S.Label>Valor da reserva</S.Label>

                            <S.Value>
                              {dayDetail?.reservePrice !== null && dayDetail?.reservePrice >= 0
                                ? mascaraMoeda(dayDetail.reservePrice.toFixed(2))
                                : '-'}
                            </S.Value>
                          </S.Item> */}

                          <S.Item>
                            <S.Label>Tarifação dinâmica</S.Label>

                            <S.Value>
                              {isDynamicPricingActive(dayDetail.dayOfWeek) ? (
                                <strong>Habilitada</strong>
                              ) : (
                                'Desabilitada'
                              )}
                            </S.Value>
                          </S.Item>
                        </S.Content>
                      );
                    })
                ) : (
                  <S.DaySetPrices>
                    Definir preços
                    <RightOutlined />
                  </S.DaySetPrices>
                )}
              </S.WrapperDay>
            ))}
          </S.WrapperWeek>
        </S.Blank>

        <S.ExceptionWrapper>
          <S.Title>Definir exceções</S.Title>

          {dynamicPricingDetail
            ?.filter(
              (item: SetPricingPageFormValues) =>
                item.exception && item.general,
            )
            .map((item: SetPricingPageFormValues) => (
              <S.ExceptionLine
                key={`exception-${item.dynamicPricingDetailUuid}`}
              >
                <S.ExceptionInfo>
                  {item.startDate
                    ? `${moment(item.startDate, 'YYYY-MM-DD').format(
                        'DD/MM/YYYY',
                      )}`
                    : '30/09/24'}

                  {item.endDate && !moment(item.startDate).isSame(item.endDate)
                    ? ` até o dia ${moment(item.endDate, 'YYYY-MM-DD').format(
                        'DD/MM/YYYY',
                      )}`
                    : ''}

                  <S.ExceptionDescription>
                    Preço customizado
                  </S.ExceptionDescription>
                </S.ExceptionInfo>

                <div>
                  {/* Sempre presente: em somente leitura vira "visualizar", senão os
                      valores da exceção ficariam inalcançáveis. O destino já abre
                      travado quando editable é falso. */}
                  <Tooltip
                    title={T(canEdit ? 'Edit exception' : 'View exception')}
                  >
                    <Button
                      shape='circle'
                      icon={
                        canEdit ? (
                          <EditOutlined style={{ color: '#9DA2AF' }} />
                        ) : (
                          <EyeOutlined style={{ color: '#9DA2AF' }} />
                        )
                      }
                      type='text'
                      onClick={() => {
                        history.push({
                          pathname: `/dynamic-pricing/${location.state.profileId}/exception`,
                          state: {
                            dynamicPricingDetail,
                            profileId: location.state.profileId,
                            profileName: location.state.profileName,
                            editable: canEdit,
                            dynamicPricingDetailUuid:
                              item.dynamicPricingDetailUuid,
                          },
                        });
                      }}
                    />
                  </Tooltip>

                  {canEdit && (
                    <Button
                      shape='circle'
                      icon={<DeleteOutlined style={{ color: '#9DA2AF' }} />}
                      type='text'
                      onClick={() =>
                        handleDeleteException(
                          item.dynamicPricingDetailUuid,
                          item.startDate || '',
                        )
                      }
                    />
                  )}
                </div>
              </S.ExceptionLine>
            ))}
          {canEdit && (
            <Row justify='end' style={{ marginTop: 32 }}>
              <Button
                shape='circle'
                icon={<PlusOutlined />}
                type='primary'
                size='large'
                onClick={() =>
                  history.push({
                    pathname: `/dynamic-pricing/${location.state.profileId}/exception`,
                    state: {
                      dynamicPricingDetail,
                      profileId: location.state.profileId,
                      profileName: location.state.profileName,
                      editable: canEdit,
                    },
                  })
                }
              />
            </Row>
          )}
        </S.ExceptionWrapper>
      </S.Wrapper>
    </S.Div>
  );
};

export default ProfileCreatePage;
